import { Button, Form } from "@constellation-academy/framework-react-components";
import { useFormik } from "formik";
import React from "react";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import {
	type EditEmailTemplateFormProps,
	type EditEmailTemplateFormState,
} from "./edit-email-template-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { MemoizedRichTextEditor } from "../../../../components/rich-text-editor";
import { RichTextEditorToolbar } from "../../../../components/rich-text-editor/rich-text-editor.const";
import { ValidatedField } from "../../../../components/ValidatedField";
import { WarningUnsavedChangesDialog } from "../../../../components/WarningUnsavedChangesDialog";
import { useCallbackPrompt } from "../../../../hooks/UseCallBackPrompt";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
	selectArrayOfEditedForms,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch, useTypedSelector } from "../../../../store/store.redux";
import { EMAIL_VARIABLES } from "../../../../translations/email";

export const EditEmailTemplateForm = ({
	variables,
	loading,
	initialValues,
	onSubmit,
}: EditEmailTemplateFormProps) => {
	const { t } = useTranslation("common");
	const dispatch = useTypedDispatch();
	const arrayOfEditedForms = useTypedSelector(selectArrayOfEditedForms);
	const formId = "EditEmailTemplateFormComponent";
	const [showPrompt, confirmNavigation, cancelNavigation, setShowPrompt] = useCallbackPrompt(
		arrayOfEditedForms.length > 0,
	);
	const formik = useFormik<EditEmailTemplateFormState>({
		initialValues: {
			subject: initialValues?.subject ?? t("emailTemplateEdit.subject"),
			preview: initialValues?.preview ?? t("emailTemplateEdit.preview"),
			body: initialValues?.body ?? t("emailTemplateEdit.body"),
		},
		validationSchema: Yup.object().shape({
			subject: Yup.string().required(t("emailTemplateEdit.subjectRequired")),
			body: Yup.string().required(t("emailTemplateEdit.bodyRequired")),
		}),
		onSubmit: (values, { setSubmitting }) => {
			onSubmit({
				...values,
			});
			setSubmitting(false);
			dispatch(resetArrayOfEditedForms());
		},
	});

	const handleOnChange = () => {
		dispatch(addEditedFormToEditedFormsArray({ form: formId }));
	};

	const handleOnClick = () => {
		formik.handleSubmit();
	};

	return (
		<>
			{showPrompt && (
				<WarningUnsavedChangesDialog
					confirmNavigation={confirmNavigation}
					setShowDialog={setShowPrompt}
					cancelNavigation={cancelNavigation}
				/>
			)}
			<Form onSubmit={formik.handleSubmit}>
				<ValidatedField<EditEmailTemplateFormState, string>
					name={"subject"}
					label={t("emailTemplateEdit.subject")}
					component={DefaultTextFieldComponent}
					onChange={handleOnChange}
					formikConfig={formik}
				/>
				<ValidatedField<EditEmailTemplateFormState, string>
					name={"preview"}
					label={t("emailTemplateEdit.preview")}
					helpText={t("emailTemplateEdit.previewHelpText")}
					component={DefaultTextFieldComponent}
					onChange={handleOnChange}
					formikConfig={formik}
				/>

				<ValidatedField<EditEmailTemplateFormState, string>
					name={"body"}
					label={t("emailTemplateEdit.body")}
					component={(props) => (
						<MemoizedRichTextEditor
							{...props}
							height={200}
							toolbar={RichTextEditorToolbar.WithTextColor}
						/>
					)}
					onChange={handleOnChange}
					formikConfig={formik}
				/>

				<h3>{t("emailTemplateEdit.availableVariables")}</h3>
				<p>{t("emailTemplateEdit.variablesDescription")}</p>
				<ul>
					{variables.map((variable) => {
						return (
							<li key={variable}>
								<strong>%%{variable}%%</strong> - {EMAIL_VARIABLES[variable]}
							</li>
						);
					})}
				</ul>

				<Button
					disabled={loading}
					content={{ label: t("actions.save") }}
					onClick={handleOnClick}
				/>
			</Form>
		</>
	);
};
